import {
  ChangeEvent,
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FileType,
  FileUploadAcceptTypes,
  type UploadedFile,
  UploadFileOptions,
  uploadMediaFile,
} from "@/lib/uploads";

import { Input } from "./ui/input";

// TODO: Consider handling multiple fileTypes in single batch?

type FileUploadContextType = {
  openFilePicker(fileType: FileType): void;
  uploadedBytes: number;
  totalBytes: number;
  isUploading: boolean;
  totalFileCount: number;
  currentFileCount: number;
  uploadProgress: number;
};

const FileUploadContext = createContext<FileUploadContextType | null>(null);

type FileUploadProviderProps = PropsWithChildren<{
  onFinish?: (files: UploadedFile[]) => void;
  // Custom uploader to override the default uploader
  onUploadFile?: (
    options: UploadFileOptions,
  ) => Promise<UploadedFile | undefined>;
  multiple?: boolean;
  accept?: string;
  maxFileSize?: number;
  minWidth?: number;
  maxWidth?: number;
  maxHeight?: number;
}>;

export const FileUploadProvider: FC<FileUploadProviderProps> = ({
  children,
  onFinish,
  onUploadFile,
  multiple = false,
  maxFileSize,
  accept,
  minWidth,
  maxWidth,
  maxHeight,
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const [selectedFileType, setSelectedFileType] = useState<FileType>();
  const [fileType, setFileType] = useState<FileType>();
  const [isUploading, setUploading] = useState(false);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [currentFileCount, setCurrentFileCount] = useState(0);

  const openFilePicker = useCallback((fileType: FileType) => {
    setSelectedFileType(fileType);
    setFileType(fileType);
  }, []);

  useEffect(() => {
    if (selectedFileType) {
      hiddenInputRef.current?.click();

      // HACK: Reset the file type to allow selecting the same files again.
      // HTML does not give us a clean way to detect if file selection was
      // cancelled on the OS level.
      setSelectedFileType(undefined);
    }
  }, [selectedFileType]);

  const handleProgress = useCallback(
    (uploadedBytes: number, totalBytes: number) => {
      setUploadedBytes(uploadedBytes);
      setTotalBytes(totalBytes);
    },
    [],
  );

  const uploadProgress = useMemo(() => {
    return Math.round((uploadedBytes / (totalBytes || 1)) * 100);
  }, [uploadedBytes, totalBytes]);

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;

      if (!files || !fileType || files.length === 0) {
        setFileType(undefined);
        return;
      }

      setTotalFileCount(files.length);
      setCurrentFileCount(0);
      setUploadedBytes(0);
      setTotalBytes(0);
      setUploading(true);

      const uploadedFiles: UploadedFile[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        setCurrentFileCount(i + 1);

        try {
          const uploadFile = onUploadFile ?? uploadMediaFile;

          const uploadedFile = await uploadFile({
            file,
            onProgress: handleProgress,
            maxFileSize,
            minWidth,
            maxWidth,
            maxHeight,
            fileType,
          });

          if (uploadedFile) {
            uploadedFiles.push(uploadedFile);
          }
        } catch (error) {
          // We do not need to rethrow here, the toast will surface the error.
          // However, we do need to reset the upload state to allow retries.

          if (i === files.length - 1) {
            setUploading(false);
            setCurrentFileCount(0);
          }
        } finally {
          setUploadedBytes(0);
          setTotalBytes(0);
        }
      }

      if (uploadedFiles.length > 0) {
        onFinish?.(uploadedFiles);
      }

      setUploading(false);
      setFileType(undefined);
    },
    [
      fileType,
      handleProgress,
      onFinish,
      maxFileSize,
      onUploadFile,
      minWidth,
      maxWidth,
      maxHeight,
    ],
  );

  return (
    <FileUploadContext.Provider
      value={{
        openFilePicker,
        uploadedBytes,
        totalBytes,
        isUploading,
        totalFileCount,
        currentFileCount,
        uploadProgress,
      }}
    >
      {children}
      {fileType && (
        <Input
          ref={hiddenInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept={accept ?? FileUploadAcceptTypes[fileType]}
          multiple={multiple}
        />
      )}
    </FileUploadContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useFileUpload = () => {
  const context = useContext(FileUploadContext);

  if (!context) {
    throw new Error("useFileUpload must be used within a FileUploadProvider");
  }

  return context;
};
